import React, {
  useState,
  ReactNode,
  useCallback,
  useEffect,
  forwardRef,
  Ref,
  useImperativeHandle,
} from 'react';

import { debounce } from 'debounce';
import { useCombobox } from 'downshift';

import { useGoogleMapsScriptTag } from '~/src/common/hooks/useGoogleMapsScriptTag';
import I18n from '~/src/common/services/I18n';
import { logger } from '~/src/common/services/Logger';
import Toaster from '~/src/common/services/Toaster';
import Tracker from '~/src/common/services/Tracker';
import { DeliveryAddress } from '~/src/common/typings/cart';
import { geocode, mapGeocoderResultToDeliveryAddress } from '~/src/common/utils/google';
import { getOs } from '~/src/common/utils/navigator';
import { Location } from '~/src/typings/addresses/types';

import { IconName } from '../Icon';
import Loader from '../Loader';

import {
  Menu,
  Item,
  Loading,
  NoResult,
  StreetNumberMissingErrorMessage,
  Container,
  SearchIcon,
  StyledInput,
  ClearIcon,
  GeolocIconWrapper,
  GeolocIcon,
  LoaderWrapper,
} from './layout';
import { GooglePlacesAutocompleteType } from './types';
import {
  getItemValue,
  getPlacePredictions,
  highlightMatch,
  hasStreetNumber,
  filterPredictions,
  getNewSessionToken,
} from './utils';

interface GooglePlacesAutocompleteProps {
  type: GooglePlacesAutocompleteType;
  inputProps: { placeholder: string; onFocus?: () => void; onBlur?: () => void };
  renderNoResult?: () => ReactNode;
  disabled?: boolean;
  onSelectResult: (newAdress: DeliveryAddress) => void;
  onChange?: (value?: string) => void;
  onClear?: () => void;
  isLoadingAddress?: boolean;
  autoFocus?: boolean;
  onMenuVisibilityChange?: (open: boolean) => void;
  iconName?: IconName;
  defaultInputValue?: string;
  withLeftIcon?: boolean;
  isGeolocationEnabled?: boolean;
}

const ERROR_MESSAGE_ID = 'NO_STREET_NUMBER';

const GooglePlacesAutocomplete = (
  {
    renderNoResult,
    disabled,
    type,
    onSelectResult,
    onChange,
    inputProps,
    onClear,
    isLoadingAddress,
    autoFocus,
    onMenuVisibilityChange,
    iconName = 'marker-normal',
    withLeftIcon = true,
    defaultInputValue,
    isGeolocationEnabled,
  }: GooglePlacesAutocompleteProps,
  ref: Ref<HTMLDivElement>,
) => {
  const [isLoading, setLoading] = useState(false);
  const [isGeolocationRunning, setIsGeolocationRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [places, setPlaces] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isStartEventSent, setStartEventSent] = useState(false);
  const [sessionToken, setSessionToken] = useState(getNewSessionToken());
  const { isGoogleMapsLoaded } = useGoogleMapsScriptTag();

  // eslint-disable-next-line
  const searchPlacesDebounced = useCallback(
    debounce(
      async (
        inputValue: string,
        sessionTokenValue: google.maps.places.AutocompleteSessionToken | null,
      ) => {
        try {
          const result = await getPlacePredictions(inputValue, type, sessionTokenValue);
          if (!result) {
            throw new Error('GooglePlacesAutocomplete: getPlacePredictions is undefined');
          }

          const isInvalid =
            result.length === 0 && type !== GooglePlacesAutocompleteType.address
              ? false
              : !hasStreetNumber(result);

          if (isInvalid) {
            setPlaces([]);

            setError(I18n.t('google-places-autocomplete.street-number-missing'));
          }

          if (!isInvalid || result.length === 0) {
            setError(null);

            const displayedResults =
              type === GooglePlacesAutocompleteType.address ? filterPredictions(result) : result;

            setPlaces(displayedResults);
          }
        } catch {
          setPlaces([]);

          setError(null);
        } finally {
          setLoading(false);
        }
      },
      1000,
    ),
    [],
  );

  useEffect(() => {
    return () => {
      searchPlacesDebounced.clear();
    };
  }, [searchPlacesDebounced]);

  const handleSendStartEvent = () => {
    if (!isStartEventSent && type === GooglePlacesAutocompleteType.address) {
      Tracker.sendEvent('shipping type started', {
        'shipping type': 'delivery',
        'shipping type step': 'enter the address',
        'event source': 'navigation',
      });

      setStartEventSent(true);
    }
  };

  const handleChange = (inputValue?: string) => {
    handleSendStartEvent();

    onChange?.(inputValue);

    if (!inputValue || inputValue.length === 0) {
      setPlaces([]);

      setError(null);
    } else {
      setLoading(true);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises  -- auto-ignored when updating eslint
      searchPlacesDebounced(inputValue, sessionToken);
    }
  };

  const handleManageResults = (results: google.maps.GeocoderResult[]) => {
    const result = results[0];

    if (
      (type === GooglePlacesAutocompleteType.address &&
        result.address_components.some(({ types }) => types.includes('street_number'))) ||
      type === GooglePlacesAutocompleteType.postalCode
    ) {
      onSelectResult(mapGeocoderResultToDeliveryAddress(result));
    } else {
      setError(I18n.t('google-places-autocomplete.street-number-missing'));
    }
  };

  const handleSelect = async (selectedItem: google.maps.places.AutocompletePrediction) => {
    if (type === GooglePlacesAutocompleteType.address) {
      Tracker.sendEvent('shipping type submitted', {
        'shipping type': 'delivery',
        'shipping type step': 'enter the address',
        'event source': 'navigation',
      });
    }

    try {
      const results = await geocode({ placeId: selectedItem.place_id });

      setSessionToken(getNewSessionToken());

      if (!results) {
        throw new Error('GooglePlacesAutocomplete: geocode is null');
      }

      handleManageResults(results);
    } catch (err) {
      logger.warning('Google Maps Geocoder Error', {
        originalError: err as Error,
      });
    }
  };

  const {
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
    openMenu,
    closeMenu,
    isOpen,
  } = useCombobox({
    items: places,
    itemToString: getItemValue,
    defaultHighlightedIndex: 0,
    onInputValueChange: changes => handleChange(changes?.inputValue),
    onIsOpenChange: changes => {
      if (changes?.isOpen !== undefined) onMenuVisibilityChange?.(changes?.isOpen);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises  -- auto-ignored when updating eslint
      if (selectedItem) handleSelect(selectedItem);

      closeMenu();
    },
    defaultInputValue: defaultInputValue ?? '',
  });

  const handleSelectGeocode = async (location: Location) => {
    closeMenu();

    try {
      Tracker.sendEvent('click cta geolocation', { 'event source': 'navigation' });

      const results = await geocode({ location });

      if (!results) {
        throw new Error('GooglePlacesAutocomplete: geocode is null');
      }

      setInputValue(results?.[0]?.formatted_address);

      if (type === GooglePlacesAutocompleteType.address) {
        Tracker.sendEvent('shipping type submitted', {
          'shipping type': 'delivery',
          'shipping type step': 'enter the address',
          'event source': 'navigation',
        });
      }

      handleManageResults(results);
    } catch (err) {
      logger.warning('Google Maps Geocoder Error', {
        originalError: err as Error,
      });
    } finally {
      setIsGeolocationRunning(false);
    }
  };

  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  useImperativeHandle(ref, () => ({
    setInputValueFromParent: (address?: DeliveryAddress) => {
      if (address) {
        setInputValue(address.formattedAddress);

        onSelectResult(address);
      }
    },
  }));

  const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handleGeoloc = () => {
    setIsGeolocationRunning(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises  -- auto-ignored when updating eslint
        handleSelectGeocode({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      // error callback
      () => {
        const isMobile = ['iOS', 'Android'].includes(getOs());
        Toaster.notify({
          icon: 'geolocation',
          message: I18n.t(
            `google-places-autocomplete.enable-location-${isMobile ? 'mobile' : 'desktop'}`,
          ),
        });

        setIsGeolocationRunning(false);
      },
    );
  };

  const isClearIconDisplayed = inputValue !== '' && !isLoadingAddress && !disabled;

  const isGeolocationIconDisplayed =
    typeof navigator === 'object' &&
    'geolocation' in navigator &&
    // geolocationAuth !== 'denied' &&
    isGeolocationEnabled &&
    !isLoadingAddress &&
    !isGeolocationRunning &&
    !isClearIconDisplayed;

  return (
    <Container>
      <div ref={ref}>
        {withLeftIcon ? <SearchIcon name={iconName} size={24} color="PRIMARY" /> : null}
        <StyledInput
          {...getInputProps({
            onFocus: () => {
              openMenu();

              inputProps?.onFocus?.();
            },
            onBlur: () => {
              inputProps?.onBlur?.();
            },
          })}
          placeholder={inputProps.placeholder}
          disabled={disabled ?? !isGoogleMapsLoaded}
          onKeyPress={handleOnKeyPress}
          autoFocus={autoFocus}
          aria-label={inputProps.placeholder}
          aria-invalid={error != null ? 'true' : 'false'}
          aria-errormessage={ERROR_MESSAGE_ID}
          $withLeftIcon={withLeftIcon}
        />
        {isClearIconDisplayed ? (
          <ClearIcon
            id="autocomplete-clear"
            name="cross"
            size={24}
            color="PRIMARY"
            onClick={() => {
              setInputValue('');

              onClear?.();
            }}
          />
        ) : null}
        {isGeolocationIconDisplayed ? (
          <GeolocIconWrapper onClick={handleGeoloc}>
            <GeolocIcon name="geolocation" color="PRIMARY" size={16} />
          </GeolocIconWrapper>
        ) : null}
        {isLoadingAddress || isGeolocationRunning ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : null}
      </div>
      <Menu {...getMenuProps()} $hasResults={places.length > 0 && isOpen}>
        {inputValue.length === 0 || error != null || !isOpen ? null : isLoading &&
          places.length === 0 ? (
          <Loading>
            {I18n.t('google-places-autocomplete.searching-for', {
              search: inputValue,
            })}
          </Loading>
        ) : (
          <>
            {places.map((item, index) => (
              <Item
                key={item.place_id}
                $isHighlighted={highlightedIndex === index}
                dangerouslySetInnerHTML={{
                  __html: highlightMatch(item.description, item.matched_substrings),
                }}
                {...getItemProps({
                  item,
                  index,
                })}
              />
            ))}
            {renderNoResult != null ? (
              <NoResult $emptyPlaces={places.length === 0}>{renderNoResult()}</NoResult>
            ) : null}
          </>
        )}
      </Menu>
      {inputValue && error != null ? (
        <StreetNumberMissingErrorMessage id={ERROR_MESSAGE_ID} role="alert">
          {error}
        </StreetNumberMissingErrorMessage>
      ) : null}
    </Container>
  );
};

export default forwardRef(GooglePlacesAutocomplete);
